import { DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ColDef,
  ColGroupDef,
  FilterModel,
  GetRowIdFunc,
  GetRowIdParams,
  GridApi,
  GridReadyEvent,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  ITextFilterParams,
  SideBarDef,
} from 'ag-grid-community';
import { Person, SearchFilter, SearchPersonsData200Response } from 'ldt-people-api';
import { NotificationService } from 'src/app/shared/notification-service/notification.service';
import * as moment from 'moment';
import { compressToEncodedURIComponent, decompressFromEncodedURIComponent } from 'lz-string';
import { AuthService, UIOrg } from 'src/app/auth/service/auth.service';
import { DarkModeService } from 'src/app/shared/dark-mode/dark-mode.service';
import { peoplePreviewGridSettings } from '../people-preview-grid-settings';
import { AlertType } from 'src/app/shared/alert/alert.component';
import { StreamsSubscribeDrawerService } from 'src/app/shared/streams-subscribe-drawer/streams-subscribe-drawer.service';
import {
  QuickBuild,
  UIFilter,
  UIFilterGroup,
  UIFilterWithSearchFilter,
} from '../quick-build.service';
import { Log } from 'src/app/logger.service';
import { peopleFullGridSettings } from '../people-full-grid-settings';
import { PeopleReportService } from '../report-service.service';
import * as dayjs from 'dayjs';

export interface PeopleDetailsComponentQueryParams {
  filter: string;
  sort: string | undefined;
}

@Component({
  selector: 'people-details',
  templateUrl: './details.component.html',
  styleUrls: ['./details.component.scss'],
  providers: [DatePipe],
  standalone: false,
})
export class PeopleDetailsComponent implements OnInit {
  @Input() readOnly: boolean = false;
  @Output() appliedFilter = new EventEmitter<PeopleDetailsComponentQueryParams>();
  orgId: string;

  refreshing: boolean = true;
  countUpdating: boolean = true;
  viewCount = 0;
  paginationToken: string | undefined;

  private gridApi: GridApi;
  rowData: Person[];
  tooltipShowDelay = 200;
  rowModelType: string = 'serverSide';
  serverSideDatasource: IServerSideDatasource = this.qbDatasource();
  cacheBlockSize = 100;
  AlertType = AlertType;
  peopleGridType: 'sample' | 'full' = 'sample';

  constructor(
    private datePipe: DatePipe,
    private notify: NotificationService,
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    public darkModeService: DarkModeService,
    private streamsDrawerService: StreamsSubscribeDrawerService,
    private reportService: PeopleReportService
  ) {}

  ngOnInit(): void {
    // Handle PAL mode. If someone exits PAL mode, the org subscription below will take care of things
    if (this.authService.palModeValue) {
      this.setGridType(undefined);

      // Access navigation state via history.state
      const navigationState = history.state as { al_config?: any; al_created_at?: string };

      // Try to load page state if someone is coming from an access link
      if (navigationState?.al_config) {
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: {
            filter: navigationState.al_config?.filter?.filter,
            sort: navigationState.al_config?.filter?.sort,
          },
          queryParamsHandling: 'merge',
        });
      }

      return;
    }

    let orgId = this.route.parent?.snapshot.paramMap.get('orgId');
    if (!orgId) {
      orgId = this.authService.getSelectedOrgIdValue;
      if (!orgId) {
        this.notify.error('Invalid path');
        this.router.navigateByUrl('/main');
      }
    }
    this.orgId = orgId;
    this.setGridType(this.authService.getSelectedOrgValue);

    this.authService.$getSelectedOrg.subscribe((org) => {
      this.setGridType(org);
      this.columnDefs =
        this.peopleGridType === 'full' ? peopleFullGridSettings : peoplePreviewGridSettings;
      this.setGridFiltersReadOnly(false);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('readOnly' in changes) {
      this.setGridFiltersReadOnly(this.readOnly);
    }
  }

  private setGridFiltersReadOnly(readonly: boolean) {
    if (!this.gridApi) {
      return;
    }

    // Recursively set filterParams for all column definitions
    const setFilterParamsRecursively = (colDefs: any[] | undefined) => {
      if (!colDefs) {
        return;
      }
      colDefs.forEach((colDef: any) => {
        if (colDef && 'field' in colDef) {
          colDef.filterParams = {
            ...(colDef.filterParams || {}),
            readOnly: readonly,
          };
        }

        // Recursively handle nested column groups
        if (colDef && colDef.children && Array.isArray(colDef.children)) {
          setFilterParamsRecursively(colDef.children);
        }
      });
    };

    const colDefs = this.gridApi.getColumnDefs();
    setFilterParamsRecursively(colDefs);
    this.gridApi.setGridOption('columnDefs', colDefs);
  }

  /// AG -GRID --------------
  private textFilterParams: ITextFilterParams = {
    filterOptions: ['startsWith', 'equals', 'notEqual', 'blank', 'notBlank'],
    defaultOption: 'startsWith',
    maxNumConditions: 1,
  };

  defaultColDef: ColDef = {
    sortable: true,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    filterParams: this.textFilterParams,
    resizable: true,
    flex: 1,
    minWidth: 100,
    enablePivot: false,
    menuTabs: ['generalMenuTab', 'filterMenuTab'],
    autoHeight: true,
  };
  sideBar: SideBarDef = {
    defaultToolPanel: 'columns',
    toolPanels: [
      {
        id: 'columns',
        labelDefault: 'Columns',
        labelKey: 'columns',
        iconKey: 'columns',
        toolPanel: 'agColumnsToolPanel',
        toolPanelParams: {
          suppressRowGroups: true,
          suppressValues: true,
          suppressPivots: true,
          suppressPivotMode: true,
          suppressColumnFilter: true,
          suppressColumnSelectAll: true,
        },
      },
    ],
  };
  columnDefs: (ColDef | ColGroupDef)[] = peoplePreviewGridSettings;
  public getRowId: GetRowIdFunc = (params: GetRowIdParams) => params.data.id;

  setGridType(org: UIOrg | undefined) {
    Log.d('app-people-details setGridType org', org);
    this.peopleGridType = 'sample';
    if (!org) {
      return;
    }
    const peopleSettings = org?.settings?.people;
    if (peopleSettings?.subType === 'unlimited') {
      this.peopleGridType = 'full';
    }
  }

  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    this.columnDefs =
      this.peopleGridType === 'full' ? peopleFullGridSettings : peoplePreviewGridSettings;

    const savedColumnState = localStorage.getItem('ag-grid-columns-people-details');

    // Get the saved column state from localstorage if a user has hidden certain columns
    // But also reset the sort order to last job change desc
    if (savedColumnState) {
      this.gridApi.closeToolPanel();

      if (this.gridApi.getColumnState().length != JSON.parse(savedColumnState || '{}').length) {
        this.notify.info(
          'Grid columns have changed, so your previous view preferences have been reset'
        );
        this.saveColumnState();
        this.gridApi.openToolPanel('columns');
      } else {
        this.gridApi.applyColumnState({ state: JSON.parse(savedColumnState || '{}') });
      }
    }

    // Get any filtering specified in the querystring and apply it
    if (this.route.snapshot.queryParams.filter) {
      this.gridApi.setFilterModel(
        JSON.parse(decompressFromEncodedURIComponent(this.route.snapshot.queryParams.filter))
      );

      // If there's a filter, we respect the sort too
      if (this.route.snapshot.queryParams.sort) {
        const dir = this.route.snapshot.queryParams.sort.startsWith('-') ? 'desc' : 'asc';
        const colId = this.route.snapshot.queryParams.sort.startsWith('-')
          ? this.route.snapshot.queryParams.sort.substring(1)
          : this.route.snapshot.queryParams.sort;
        this.gridApi.applyColumnState({
          state: [{ colId: colId, sort: dir }],
        });
      }
    } else {
      // No querystring items, so set a default sort
      this.gridApi.applyColumnState({
        state: [{ colId: 'company_change_detected_at', sort: 'desc' }],
      });
    }

    this.setGridFiltersReadOnly(this.readOnly);
  }

  saveColumnState() {
    if (this.gridApi) {
      const currentState = this.gridApi.getColumnState();
      localStorage.setItem('ag-grid-columns-people-details', JSON.stringify(currentState));
    }
  }

  agGridFiltersToQuickBuild(filterModel: any): QuickBuild {
    let searchFilters: (UIFilter | UIFilterGroup)[] = [];
    let jobsFilters: UIFilterGroup = {
      filters: [],
      operator: 'and',
    };

    Object.keys(filterModel).forEach((fieldName: any) => {
      var match_type;
      var search_type;

      let filter: UIFilterWithSearchFilter = { filter: { field: fieldName } as SearchFilter };

      switch (filterModel[fieldName].filterType) {
        case 'set':
          if (filterModel[fieldName].values.length == 0) {
            return;
          }

          filter.filter.match_type = SearchFilter.MatchTypeEnum.Exact;
          filter.filter.type = SearchFilter.TypeEnum.Must;
          filter.filter.string_values = filterModel[fieldName].values;
          break;

        case 'text':
          match_type = SearchFilter.MatchTypeEnum.Exact;
          search_type = SearchFilter.TypeEnum.Must;

          switch (filterModel[fieldName].type) {
            case 'startsWith':
              match_type = SearchFilter.MatchTypeEnum.Fuzzy;
              search_type = SearchFilter.TypeEnum.Must;
              filter.filter.string_values = filterModel[fieldName].filter.split(',');
              break;
            case 'equals':
              match_type = SearchFilter.MatchTypeEnum.Exact;
              search_type = SearchFilter.TypeEnum.Must;
              filter.filter.string_values = filterModel[fieldName].filter.split(',');
              break;
            case 'notEqual':
              match_type = SearchFilter.MatchTypeEnum.Exact;
              search_type = SearchFilter.TypeEnum.MustNot;
              filter.filter.string_values = filterModel[fieldName].filter.split(',');
              break;
            case 'blank':
              match_type = SearchFilter.MatchTypeEnum.Exists;
              search_type = SearchFilter.TypeEnum.MustNot;
              break;
            case 'notBlank':
              match_type = SearchFilter.MatchTypeEnum.Exists;
              search_type = SearchFilter.TypeEnum.Must;
              break;
          }

          filter.filter.match_type = match_type;
          filter.filter.type = search_type;

          break;
        case 'number':
          var search_min;
          var search_max;
          switch (filterModel[fieldName].type) {
            case 'equals':
              search_type = SearchFilter.TypeEnum.Must;
              search_min = filterModel[fieldName].filter;
              search_max = filterModel[fieldName].filter;
              break;
            case 'notEqual':
              search_type = SearchFilter.TypeEnum.MustNot;
              search_min = filterModel[fieldName].filter;
              search_max = filterModel[fieldName].filter;
              break;
            case 'lessThan':
              search_type = SearchFilter.TypeEnum.Must;
              search_max = filterModel[fieldName].filter - 1;
              break;
            case 'lessThanOrEqual':
              search_type = SearchFilter.TypeEnum.Must;
              search_max = filterModel[fieldName].filter;
              break;
            case 'greaterThan':
              search_type = SearchFilter.TypeEnum.Must;
              search_min = filterModel[fieldName].filter - 1;
              break;
            case 'greaterThanOrEqual':
              search_type = SearchFilter.TypeEnum.Must;
              search_min = filterModel[fieldName].filter;
              break;
            case 'inRange':
              search_type = SearchFilter.TypeEnum.Must;
              search_min = filterModel[fieldName].filter;
              search_max = filterModel[fieldName].filterTo;
              break;
            case 'blank':
              match_type = SearchFilter.MatchTypeEnum.Exists;
              search_type = SearchFilter.TypeEnum.MustNot;
              break;
            case 'notBlank':
              match_type = SearchFilter.MatchTypeEnum.Exists;
              search_type = SearchFilter.TypeEnum.Must;
              break;
          }

          filter.filter.match_type = match_type;
          filter.filter.type = search_type;

          if (search_min) filter.filter.number_min = search_min;
          if (search_max) filter.filter.number_max = search_max;
          if (match_type) filter.filter.match_type = match_type;

          break;
        case 'date':
          var search_from;
          var search_to;
          const parsed_from = filterModel[fieldName].dateFrom
            ? moment(new Date(filterModel[fieldName].dateFrom)).format('YYYY-MM-DD')
            : undefined;
          const parsed_to = filterModel[fieldName].dateTo
            ? moment(new Date(filterModel[fieldName].dateTo)).format('YYYY-MM-DD')
            : undefined;

          switch (filterModel[fieldName].type) {
            case 'equals':
              search_type = SearchFilter.TypeEnum.Must;
              search_from = parsed_from;
              search_to = parsed_from;
              break;
            case 'notEqual':
              search_type = SearchFilter.TypeEnum.MustNot;
              search_from = parsed_from;
              search_to = parsed_from;
              break;
            case 'lessThanOrEqualsCustom':
              search_type = SearchFilter.TypeEnum.Must;
              search_to = parsed_from;
              break;
            case 'greaterThanOrEqualsCustom':
              search_type = SearchFilter.TypeEnum.Must;
              search_from = parsed_from;
              break;
            case 'inRange':
              search_type = SearchFilter.TypeEnum.Must;
              search_from = parsed_from;
              search_to = parsed_to;
              break;
            case 'blank':
              match_type = SearchFilter.MatchTypeEnum.Exists;
              search_type = SearchFilter.TypeEnum.MustNot;
              break;
            case 'notBlank':
              match_type = SearchFilter.MatchTypeEnum.Exists;
              search_type = SearchFilter.TypeEnum.Must;
              break;
            default:
              search_type = SearchFilter.TypeEnum.Must;
              search_from = parsed_from;
              break;
          }

          filter.filter.match_type = match_type;
          filter.filter.type = search_type;

          if (search_from) filter.filter.date_from = search_from;
          if (search_to) filter.filter.date_to = search_to;
          if (match_type) filter.filter.match_type = match_type;

          break;
      }

      if (fieldName.startsWith('jobs.')) {
        jobsFilters.filters!.push(filter);
      } else {
        searchFilters.push(filter);
      }
    });

    if (jobsFilters.filters!.length > 0) {
      searchFilters.push(jobsFilters);
    }

    const rootFilterGroup: UIFilterGroup = {
      operator: 'and',
      filters: searchFilters,
    };
    const quickBuild: QuickBuild = {
      id: 'people-details',
      name: 'People Details',
      description: 'People Details',
      rootFilterGroup: rootFilterGroup,
    };

    return quickBuild;
  }

  qbDatasource() {
    return {
      getRows: (params: IServerSideGetRowsParams) => {
        params.api.hideOverlay();
        const size = (params.request.endRow || 0) - (params.request.startRow || 0);

        // If the grid is back at the top, reset the pagination token
        if (params.request.startRow === 0) {
          delete this.paginationToken;
          this.countUpdating = true;
        }

        var sortBy = undefined;
        if (params.request.sortModel.length > 0) {
          sortBy = params.request.sortModel[0].colId;
          if (params.request.sortModel[0].sort == 'desc') {
            sortBy = '-' + sortBy;
          }
        }

        if (Object.keys(params.request.filterModel as FilterModel).length > 0) {
          const encodedFilter = compressToEncodedURIComponent(
            JSON.stringify(params.request.filterModel)
          );
          const queryParams = {
            ...this.route.snapshot.queryParams,
            filter: encodedFilter,
            sort: sortBy,
          };
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: queryParams,
            queryParamsHandling: 'merge',
          });
          this.appliedFilter.emit({ filter: encodedFilter, sort: sortBy });
        } else {
          const queryParams = { ...this.route.snapshot.queryParams };
          delete queryParams.filter;
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: queryParams,
          });
          this.appliedFilter.emit(undefined);
        }

        const qb = this.agGridFiltersToQuickBuild(params.request.filterModel);

        this.reportService.startCacheContext();
        this.reportService
          .getPeopleData(
            qb,
            this.peopleGridType === 'sample',
            sortBy || '',
            size,
            this.paginationToken
          )
          .subscribe({
            next: (res) => {
              params.success({
                rowData: res?.people || [],
              });
              if ('pagination_token' in res && this.peopleGridType !== 'sample') {
                this.paginationToken =
                  (res as SearchPersonsData200Response).pagination_token || undefined;
              } else {
                this.paginationToken = undefined;
              }
              this.viewCount = res.count || 0;
              this.refreshing = false;
              this.countUpdating = false;
            },
            error: (error: any) => {
              params.fail();
              this.viewCount = 0;
              this.refreshing = false;
              this.countUpdating = false;

              if (error.status === 402) {
                return;
              }
              this.notify.error('Oops. There was an error getting the data.');
            },
          });
      },
    };
  }

  refreshData() {
    if (this.gridApi) {
      this.refreshing = true;
      this.gridApi.refreshServerSide({ purge: true });
    }
  }

  downloadData() {
    const quickBuild = this.agGridFiltersToQuickBuild(this.gridApi.getFilterModel());
    this.streamsDrawerService.openDrawer({
      quickBuild: quickBuild,
      mode: 'data-selection',
      matchingRecords: this.viewCount,
    });
  }
}
